'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export interface Order {
  id: string;
  orderNumber: string;
  customerName: string;
  items: string[];
  total: number;
  status: 'pending' | 'preparing' | 'ready' | 'completed';
  orderType: 'adhoc' | 'wholesale';
  deliveryMethod: 'pickup' | 'delivery';
  time: string;
  date: string;
}

interface OrdersTableProps {
  orders: Order[];
  onStatusChange: (orderId: string, newStatus: Order['status']) => void;
}

export default function OrdersTable({ orders, onStatusChange }: OrdersTableProps) {
  const [filter, setFilter] = useState<'all' | Order['status']>('all');

  const filteredOrders =
    filter === 'all' ? orders : orders.filter((order) => order.status === filter);

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'preparing':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'ready':
        return 'bg-success/10 text-success border-success/20';
      case 'completed':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const statusOptions: Order['status'][] = ['pending', 'preparing', 'ready', 'completed'];

  return (
    <div className="glass-card rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border flex items-center justify-between">
        <h2 className="text-2xl font-black text-foreground font-heading">Recent Orders</h2>

        {/* Filter Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
              filter === 'all' ?'bg-primary text-primary-foreground' :'bg-accent text-accent-foreground hover:bg-primary/10'
            }`}
          >
            All
          </button>
          {statusOptions.map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                filter === status
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-accent text-accent-foreground hover:bg-primary/10'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-accent">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">
                Order #
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">
                Items
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">
                Time
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">
                Total
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredOrders.map((order) => (
              <tr key={order.id} className="hover:bg-accent/50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="font-bold text-foreground">{order.orderNumber}</span>
                </td>
                <td className="px-6 py-4">
                  <div>
                    <p className="font-semibold text-foreground">{order.customerName}</p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {order.deliveryMethod}
                    </p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <p className="text-sm text-muted-foreground">
                    {order.items.slice(0, 2).join(', ')}
                    {order.items.length > 2 && ` +${order.items.length - 2} more`}
                  </p>
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                      order.orderType === 'wholesale' ?'bg-secondary/10 text-secondary' :'bg-primary/10 text-primary'
                    }`}
                  >
                    {order.orderType}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <p className="text-sm font-medium text-foreground">{order.time}</p>
                  <p className="text-xs text-muted-foreground">{order.date}</p>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-lg font-black text-primary">
                    ${order.total.toFixed(2)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${getStatusColor(
                      order.status
                    )}`}
                  >
                    {order.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {order.status !== 'completed' && (
                      <button
                        onClick={() => {
                          const nextStatus: Record<Order['status'], Order['status']> = {
                            pending: 'preparing',
                            preparing: 'ready',
                            ready: 'completed',
                            completed: 'completed',
                          };
                          onStatusChange(order.id, nextStatus[order.status]);
                        }}
                        className="p-2 rounded-lg bg-success/10 hover:bg-success/20 transition-colors"
                        aria-label="Advance order status"
                      >
                        <Icon name="CheckIcon" size={16} className="text-success" />
                      </button>
                    )}
                    <button
                      className="p-2 rounded-lg bg-accent hover:bg-primary/10 transition-colors"
                      aria-label="Print receipt"
                    >
                      <Icon name="PrinterIcon" size={16} className="text-foreground" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredOrders.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="InboxIcon" size={48} className="text-muted mx-auto mb-4" />
          <p className="text-muted-foreground">No orders found for this filter</p>
        </div>
      )}
    </div>
  );
}